/* Navigation presentation only. Commerce remains owned by Ella. */
if (!customElements.get('kanawa-header')) {
  customElements.define('kanawa-header', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      // Ella can move focus to a close button after a pointer-triggered animation.
      // Track input modality without changing focus traps or keyboard navigation.
      document.addEventListener('pointerdown', () => this.setAttribute('data-kw-pointer-focus', ''), {signal, capture:true, passive:true});
      document.addEventListener('keydown', (event) => {
        if (!event.altKey && !event.ctrlKey && !event.metaKey) this.removeAttribute('data-kw-pointer-focus');
      }, {signal, capture:true});
      this.toggle = this.querySelector('[data-kw-toggle]');
      this.navigation = this.querySelector('.kw-navigation');
      this.shell = this.querySelector('.kw-shell');
      this.backdrop = this.querySelector('.kw-backdrop');
      this.media = matchMedia('(min-width:1025px)');
      // Use Ella's translated, server-rendered count; never infer it from prices or events.
      this.syncCartLabel = () => {
        const cart = this.querySelector('#cart-icon-bubble');
        if (!cart) return;
        const label = cart.querySelector('[data-cart-text]')?.textContent.trim()
          || this.dataset.emptyCartLabel;
        if (label && cart.getAttribute('aria-label') !== label) cart.setAttribute('aria-label', label);
        const menuCart = this.querySelector('[data-kw-menu-cart]');
        const menuCount = this.querySelector('[data-kw-menu-cart-count]');
        if (menuCart && label) menuCart.setAttribute('aria-label', label);
        const badge = cart.querySelector('.cart-count-bubble');
        const badgeMarkup = badge?.outerHTML || '';
        if (menuCount && this.menuCartMarkup !== badgeMarkup) {
          menuCount.replaceChildren(...(badge ? [badge.cloneNode(true)] : []));
          this.menuCartMarkup = badgeMarkup;
        }
      };
      this.cartObserver = new MutationObserver(this.syncCartLabel);
      const cartRegion = this.querySelector('.kw-cart');
      if (cartRegion) this.cartObserver.observe(cartRegion, { childList:true, subtree:true, characterData:true });
      this.syncCartLabel();
      this.addEventListener('click', (event) => this.onClick(event), { signal, capture:true });
      this.addEventListener('keydown', (event) => this.onKey(event), { signal });
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-kw-localization]')) event.target.form.requestSubmit();
      }, { signal });
      this.media.addEventListener('change', () => this.close(true), { signal });
      window.addEventListener('pagehide', () => this.close(true), { signal });
      window.addEventListener('pageshow', (event) => { if (event.persisted) this.close(true); }, { signal });
      // Reserve only the visible Shopify preview bar; do not modify the bar itself.
      this.syncPreviewClearance = () => {
        const bar = document.getElementById('PBarNextFrameWrapper');
        if (bar !== this.previewBar) {
          this.previewResize?.disconnect();
          this.previewBar = bar;
          if (bar) this.previewResize.observe(bar);
        }
        const rect = bar?.getBoundingClientRect();
        const clearance = rect && rect.height > 0 && rect.top > innerHeight / 2
          ? Math.max(0, innerHeight - rect.top) : 0;
        this.style.setProperty('--kw-preview-clearance', `${Math.ceil(clearance)}px`);
      };
      this.previewResize = new ResizeObserver(this.syncPreviewClearance);
      this.previewMount = new MutationObserver(this.syncPreviewClearance);
      this.previewMount.observe(document.body, {childList:true});
      window.addEventListener('resize', this.syncPreviewClearance, {signal});
      this.syncPreviewClearance();
      if (this.hasAttribute('data-overlay')) {
        this.positionOverlay = () => {
          const main = document.querySelector('main, #MainContent');
          const section = this.closest('.shopify-section');
          if (!main || !section) return;
          const gap = this.media.matches ? (this.hasAttribute('data-full-width') ? 0 : 24) : 12;
          const top = main.getBoundingClientRect().top - section.getBoundingClientRect().top + gap;
          this.style.setProperty('--kw-overlay-top', `${top}px`);
        };
        this.overlayObserver = new ResizeObserver(this.positionOverlay);
        const main = document.querySelector('main, #MainContent');
        const group = this.closest('#header-group');
        if (main) this.overlayObserver.observe(main);
        if (group) this.overlayObserver.observe(group);
        window.addEventListener('resize', this.positionOverlay, { signal });
        window.addEventListener('load', this.positionOverlay, { signal });
        this.positionOverlay();
      }
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) this.cleanupDrawers();
      }, { signal });
      // Ella moves these nodes to body; record ownership for Theme Editor reloads.
      queueMicrotask(() => this.claimDrawers());
    }

    claimDrawers() {
      if (!this.isConnected) return;
      this.ownedDrawers ||= new Set();
      const owner = this.closest('.shopify-section');
      for (const id of ['Drawer-Search', 'Drawer-Account']) {
        const local = owner?.querySelector(`[id="${id}"]`);
        const candidates = [...document.querySelectorAll(`[id="${id}"]`)];
        const drawer = local || candidates.find(node => !node.dataset.kwOwner);
        if (drawer) {
          drawer.dataset.kwOwner = this.dataset.sectionId;
          this.ownedDrawers.add(drawer);
        }
      }
    }

    cleanupDrawers() {
      for (const drawer of this.ownedDrawers || []) {
        if (drawer.classList.contains('open')) {
          if (typeof removeTrapFocus === 'function') removeTrapFocus();
          document.body.classList.remove('overflow-hidden');
          document.documentElement.removeAttribute('scroll-lock');
        }
        drawer.remove();
      }
      this.ownedDrawers?.clear();
    }

    disconnectedCallback() {
      this.previewResize?.disconnect();
      this.previewMount?.disconnect();
      this.close(true, false);
      this.overlayObserver?.disconnect();
      this.cartObserver?.disconnect();
      this.panelAnimation?.cancel();
      // Reordering a section may synchronously disconnect and reconnect it.
      // Remove only this instance's owned nodes, never a replacement section's drawers.
      queueMicrotask(() => { if (!this.isConnected) this.cleanupDrawers(); });
      this.abort?.abort();
      this.abort = null;
    }

    onClick(event) {
      const button = event.target.closest('button, a, [data-kw-dismiss]');
      if (!button) return;
      if (button.matches('[data-kw-toggle]')) {
        this.hasAttribute('data-open') ? this.close() : this.open();
      } else if (button.matches('[data-kw-dismiss]')) {
        this.close();
      } else if (button.hasAttribute('data-kw-panel')) {
        this.showPanel(button.dataset.kwPanel, true);
      } else if (button.hasAttribute('data-kw-back')) {
        const previous = this.getAttribute('data-level');
        this.showPanel(button.dataset.kwBack, false);
        this.querySelector(`[data-kw-panel="${CSS.escape(previous || '')}"]`)?.focus();
      } else if (button.matches('.cart-icon--drawer')) {
        // Ella owns cart opening. Release navigation before its click handler runs.
        this.close(true, false);
      } else if (button.closest('side-drawer-opener')) {
        // Release this focus trap and scroll lock BEFORE Ella's opener runs.
        const opener = button.closest('side-drawer-opener');
        const drawer = document.querySelector(opener.dataset.sideDrawer);
        this.close(true, false);
        // An opener inside the mobile menu becomes hidden. Return from Ella to the visible menu toggle.
        if (this.navigation.contains(button) && drawer) {
          // A timer runs after Ella's button listener has stored its own opener.
          // A microtask can run between capture and target listeners and be overwritten.
          setTimeout(() => { if (this.isConnected) drawer.setActiveElement?.(this.toggle); }, 0);
        }
      }
    }

    open() {
      this.syncPreviewClearance?.();
      clearTimeout(this.closeTimer);
      this.navigation.hidden = false;
      this.navigation.inert = false;
      const toolbarCart = this.querySelector('.kw-toolbar .kw-cart');
      if (toolbarCart && !this.media.matches) toolbarCart.inert = true;
      this.backdrop.hidden = false;
      this.backdrop.inert = false;
      const top = Math.max(8, this.getBoundingClientRect().top);
      this.style.setProperty('--kw-top', `${top}px`);
      this.shell.setAttribute('role', 'dialog');
      this.shell.setAttribute('aria-modal', 'true');
      this.shell.setAttribute('aria-label', this.dataset.menuLabel);
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute('aria-label', this.dataset.closeLabel);
      document.documentElement.classList.add('kw-nav-locked');
      // Read before writing data-open so entrance transitions also run on first open.
      this.navigation.getBoundingClientRect();
      this.setAttribute('data-open', '');
      if (this.media.matches) {
        const first = this.querySelector('.kw-main [data-kw-panel]');
        if (first) this.showPanel(first.dataset.kwPanel, false);
      }
      this.toggle.focus();
    }

    close(immediate = false, restoreFocus = true) {
      if (!this.navigation) return;
      const wasOpen = this.hasAttribute('data-open');
      this.removeAttribute('data-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.setAttribute('aria-label', this.dataset.menuLabel);
      this.shell.removeAttribute('role');
      this.shell.removeAttribute('aria-modal');
      this.shell.removeAttribute('aria-label');
      this.navigation.inert = true;
      const toolbarCart = this.querySelector('.kw-toolbar .kw-cart');
      if (toolbarCart) toolbarCart.inert = false;
      this.backdrop.inert = true;
      document.documentElement.classList.remove('kw-nav-locked');
      clearTimeout(this.closeTimer);
      const finish = () => { this.navigation.hidden = true; this.backdrop.hidden = true; this.showPanel('', false); };
      if (immediate || matchMedia('(prefers-reduced-motion:reduce)').matches) finish();
      else this.closeTimer = setTimeout(finish, 400);
      if (wasOpen && restoreFocus) this.toggle.focus();
    }

    showPanel(id, focus) {
      const previousId = this.getAttribute('data-level') || '';
      const panel = id ? this.querySelector(`[id="${CSS.escape(id)}"]`) : null;
      if (id && !panel) return;
      for (const node of this.querySelectorAll('.kw-subpanel')) node.hidden = node !== panel;
      for (const trigger of this.querySelectorAll('[data-kw-panel]')) {
        trigger.setAttribute('aria-expanded', String(trigger.dataset.kwPanel === id));
      }
      if (panel) this.setAttribute('data-level', id);
      else this.removeAttribute('data-level');
      this.panelAnimation?.cancel();
      if (previousId !== id && this.hasAttribute('data-open') && !matchMedia('(prefers-reduced-motion:reduce)').matches) {
        const incoming = panel || this.querySelector('.kw-main');
        const direction = focus ? 1 : -1;
        this.panelAnimation = incoming?.animate([
          { opacity:0, transform:`translateX(${direction * 8}px)` },
          { opacity:1, transform:'translateX(0)' }
        ], { duration:240, easing:'cubic-bezier(.22,.61,.36,1)' });
      }
      if (focus) {
        this.querySelector('.kw-navigation-scroll').scrollTop = 0;
        panel?.querySelector('h2')?.focus();
      }
    }

    onKey(event) {
      if (!this.hasAttribute('data-open')) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); }
      if (event.key !== 'Tab') return;
      const nodes = [...this.shell.querySelectorAll('a[href],button,select,input,[tabindex="0"]')]
        .filter(node => !node.disabled && !node.closest('[hidden],[inert]') && node.getClientRects().length);
      const first = nodes[0], last = nodes.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  });
}
